/**
 * Website analytics. odo's own script measures visits on your sites; Stripe
 * payments are traced back to the visits that brought them (attribution).
 * Owner API calls take `?project=<id>` like everything else.
 */
import { z } from 'zod';
import type { InteractionItem } from './interactions';

// ─── Sites ──────────────────────────────────────────────────────────────────

/**
 * `https://www.Example.com/pricing` → `example.com`. Returns null for anything
 * that isn't a host name (IP addresses and `localhost` are allowed for testing).
 */
export function normalizeHost(input: string): string | null {
  let value = input.trim().toLowerCase();
  if (!value) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//.test(value)) value = `https://${value}`;
  let host: string;
  try {
    host = new URL(value).hostname;
  } catch {
    return null;
  }
  host = host.replace(/^www\./, '').replace(/\.$/, '');
  if (host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return host;
  return /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/.test(host)
    ? host
    : null;
}

const hostSchema = z
  .string()
  .max(300)
  .transform((v, ctx) => {
    const host = normalizeHost(v);
    if (!host) ctx.addIssue({ code: 'custom', message: 'Enter a domain like example.com' });
    return host ?? '';
  });

/** `/blog/*` style: `*` is any run of characters (slashes too). */
export const pathPatternSchema = z
  .string()
  .trim()
  .min(1, 'Enter a path')
  .max(200)
  .refine((v) => v.startsWith('/') || v.startsWith('*'), 'Paths start with /')
  // Each * is a wildcard tried against every hit's path: a few are plenty.
  .refine((v) => (v.match(/\*/g)?.length ?? 0) <= 4, 'Use at most four *');

export const siteSettingsSchema = z.object({
  /**
   * Store nothing in visitors' browsers. Needs no consent banner, but a visitor
   * is only recognized within a day, so payments can't be traced to visits.
   */
  cookieless: z.boolean(),
  /** More hosts that count as this site, besides its domain and subdomains. */
  hosts: z.array(hostSchema).max(20),
  /** Pages that aren't measured. */
  exclude: z.array(pathPatternSchema).max(50),
  /** Ad networks' link domains: a click on a link to one counts as an Ad click, not an outbound link. */
  adHosts: z.array(hostSchema).max(20),
  /**
   * Keep what people do on its pages (clicks, forms, script errors, sections seen, page speed,
   * experiments: shared/interactions.ts). Off: the script's reports of them are dropped.
   */
  interactions: z.boolean(),
});
export type SiteSettings = z.infer<typeof siteSettingsSchema>;

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  cookieless: false,
  hosts: [],
  exclude: [],
  adHosts: [],
  interactions: true,
};

/** The script's name for a click on a link to another site. */
export const OUTBOUND_EVENT = 'Outbound link';
/** A click on a link to one of the site's ad domains. */
export const AD_CLICK_EVENT = 'Ad click';

/** Whether a link goes to one of these domains (or a subdomain of one). */
export function isAdLink(url: unknown, adHosts: string[]): boolean {
  if (typeof url !== 'string' || !adHosts.length) return false;
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return false;
  }
  return adHosts.some((d) => host === d || host.endsWith(`.${d}`));
}

export const createSiteSchema = z.object({ domain: hostSchema });
export const updateSiteSchema = z
  .object({ domain: hostSchema.optional(), settings: siteSettingsSchema.partial().optional() })
  .refine((v) => v.domain !== undefined || v.settings !== undefined, 'Nothing to change');
export type UpdateSiteInput = z.input<typeof updateSiteSchema>;

export interface SiteDto {
  /** The snippet's `data-site`. */
  id: string;
  projectId: string;
  domain: string;
  settings: SiteSettings;
  createdAt: number;
  /** The latest visit received; null until the script is installed. */
  lastSeenAt: number | null;
}

// ─── Goals & funnels ────────────────────────────────────────────────────────

export const GOAL_KINDS = ['page', 'event'] as const;
export type GoalKind = (typeof GOAL_KINDS)[number];

/** A custom event's name, as the site sends it with `odo('name')`. */
export const eventNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter an event name')
  .max(80, 'Keep it under 80 characters');

const matchFor = (kind: GoalKind, match: string) =>
  kind === 'page' ? pathPatternSchema.safeParse(match) : eventNameSchema.safeParse(match);

const stepShape = z.object({ kind: z.enum(GOAL_KINDS), match: z.string().trim().min(1).max(200) });

export const goalSchema = stepShape
  .extend({ name: z.string().trim().min(1, 'Give it a name').max(60) })
  .superRefine((v, ctx) => {
    const res = matchFor(v.kind, v.match);
    if (!res.success)
      ctx.addIssue({ code: 'custom', path: ['match'], message: res.error.issues[0]!.message });
  });
export type GoalInput = z.infer<typeof goalSchema>;

export interface GoalDto extends GoalInput {
  id: string;
  projectId: string;
  createdAt: number;
}

export const funnelStepSchema = stepShape.superRefine((v, ctx) => {
  const res = matchFor(v.kind, v.match);
  if (!res.success) ctx.addIssue({ code: 'custom', path: ['match'], message: res.error.issues[0]!.message });
});
export type FunnelStep = z.infer<typeof funnelStepSchema>;

export const funnelSchema = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(60),
  steps: z.array(funnelStepSchema).min(2, 'Add at least two steps').max(8, 'Up to eight steps'),
});
export type FunnelInput = z.infer<typeof funnelSchema>;

export interface FunnelDto extends FunnelInput {
  id: string;
  projectId: string;
  createdAt: number;
}

/** Whether `path` matches a page pattern: exact, or with `*` standing for anything. */
export function matchesPath(pattern: string, path: string): boolean {
  if (!pattern.includes('*')) return pattern === path;
  let re = patterns.get(pattern);
  if (!re) {
    // Runs of * are one wildcard.
    re = new RegExp(`^${pattern.replace(/\*+/g, '*').split('*').map(escapeRegExp).join('.*')}$`);
    if (patterns.size > 1000) patterns.clear();
    patterns.set(pattern, re);
  }
  return re.test(path);
}

const patterns = new Map<string, RegExp>();

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ─── Queries ────────────────────────────────────────────────────────────────

export const TRAFFIC_RANGES = ['today', '7d', '30d', '90d', '12m'] as const;
export type TrafficRange = (typeof TRAFFIC_RANGES)[number];

/**
 * What a range is compared with: the span just before it (yesterday for
 * today), the same day a week earlier (today only), the same dates a year
 * earlier, or nothing.
 */
export const COMPARES = ['previous', 'week', 'year', 'none'] as const;
export type Compare = (typeof COMPARES)[number];

/** Which visit gets the credit for a payment: the first ever, or the last one that came from somewhere. */
export const ATTRIBUTION_MODELS = ['first', 'last'] as const;
export type AttributionModel = (typeof ATTRIBUTION_MODELS)[number];

/** What a dashboard can be narrowed to (tap a row to filter by it). */
export const FILTER_KEYS = [
  'channel',
  'source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'page',
  'entry',
  'exit',
  'country',
  'region',
  'city',
  'device',
  'browser',
  'os',
  'goal',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];
export type TrafficFilters = Partial<Record<FilterKey, string>>;

export const FILTER_LABELS: Record<FilterKey, string> = {
  channel: 'Channel',
  source: 'Source',
  utm_source: 'UTM source',
  utm_medium: 'UTM medium',
  utm_campaign: 'Campaign',
  utm_content: 'Content',
  page: 'Page',
  entry: 'Entry page',
  exit: 'Exit page',
  country: 'Country',
  region: 'Region',
  city: 'City',
  device: 'Device',
  browser: 'Browser',
  os: 'OS',
  goal: 'Goal',
};

export const trafficQuerySchema = z.object({
  range: z.enum(TRAFFIC_RANGES).default('30d'),
  /** One site of the scope's, or all of them. */
  site: z
    .string()
    .regex(/^[\w-]{6,40}$/)
    .optional(),
  model: z.enum(ATTRIBUTION_MODELS).default('last'),
  compare: z.enum(COMPARES).default('previous'),
  filters: z.partialRecord(z.enum(FILTER_KEYS), z.string().max(300)).default({}),
});
export type TrafficQuery = z.infer<typeof trafficQuerySchema>;

/** `?range=30d&site=…&f.country=DE` ⇄ a query. */
export function trafficSearch(q: Partial<TrafficQuery>): string {
  const params = new URLSearchParams();
  if (q.range) params.set('range', q.range);
  if (q.site) params.set('site', q.site);
  if (q.model) params.set('model', q.model);
  if (q.compare && q.compare !== 'previous') params.set('compare', q.compare);
  for (const [k, v] of Object.entries(q.filters ?? {})) if (v !== undefined) params.set(`f.${k}`, v);
  return params.toString();
}

export function parseTrafficSearch(params: URLSearchParams): unknown {
  const filters: Record<string, string> = {};
  for (const [k, v] of params) if (k.startsWith('f.')) filters[k.slice(2)] = v;
  return {
    range: params.get('range') ?? undefined,
    site: params.get('site') || undefined,
    model: params.get('model') ?? undefined,
    compare: params.get('compare') ?? undefined,
    filters,
  };
}

export type Granularity = 'hour' | 'day' | 'month';

export interface TrafficTotals {
  visitors: number;
  visits: number;
  pageviews: number;
  /** Share of visits that saw one page and did nothing else (0–1). */
  bounceRate: number;
  /** Average time actually spent on the site per visit (tab visible), seconds. */
  duration: number;
  /** Attributed revenue, display currency. */
  revenue: number;
  /** Visitors who paid. */
  customers: number;
  /** customers ÷ visitors (0–1). */
  conversionRate: number;
  /** Revenue per visitor. */
  rpv: number;
}

export type TrafficMetric = keyof Omit<TrafficTotals, 'customers'>;

export interface TrafficPoint {
  /** `YYYY-MM-DDTHH` (hours), `YYYY-MM-DD` (days) or `YYYY-MM` (months), local time. */
  key: string;
  /**
   * When the bucket begins and ends, as real instants: [start, end). Match other data to a
   * bucket by these rather than by its place in the list: when the clocks go back, the hour
   * they repeat is one bucket two hours long, and when they go forward the hour they skip
   * isn't there. The current range's last bucket runs to its natural end, past `range.to`.
   */
  start: number;
  end: number;
  visitors: number;
  visits: number;
  pageviews: number;
  bounceRate: number;
  duration: number;
  revenue: number;
  conversionRate: number;
  rpv: number;
}

export interface BreakdownRow {
  /** What a filter on this row matches. */
  key: string;
  visitors: number;
  /** Visits (sessions); for pages, views. */
  count: number;
  revenue: number;
  customers: number;
  /** customers ÷ visitors. */
  conversionRate: number;
  /** Pages: average engaged seconds per view; elsewhere per visit. */
  duration?: number;
  bounceRate?: number;
}

export interface GoalResult {
  id: string;
  name: string;
  kind: GoalKind;
  match: string;
  /** Visitors who reached it. */
  visitors: number;
  /** Times it happened. */
  count: number;
  conversionRate: number;
  /** Revenue from visitors who reached it. */
  revenue: number;
}

/**
 * How a payment was matched to its visitor: the platform that handed the
 * visitor id back with the sale (each one's own passthrough: Stripe metadata
 * and Payment Links, Lemon Squeezy custom data, Polar metadata…), or `event`,
 * the site's own `odo('Purchase')` call.
 */
export const CONVERSION_VIAS = [
  'event',
  'stripe',
  'lemon_squeezy',
  'polar',
  'paddle',
  'gumroad',
  'dodo_payments',
  'shopify',
  'woocommerce',
] as const;
export type ConversionVia = (typeof CONVERSION_VIAS)[number];

/** A paying visitor: what they paid and how they found the site. */
export interface CustomerJourney {
  /** The payment (conversion) id. */
  id: string;
  at: number;
  /** Display currency (negative for a refund). */
  amount: number;
  /** Name or email from Stripe; hidden from shared views. */
  customer: string | null;
  source: string | null;
  channel: string;
  campaign: string | null;
  landing: string;
  country: string | null;
  device: string;
  /** Visits before paying. */
  visits: number;
  /** First visit to payment, ms. */
  timeToConvert: number;
  /** The visit credited under the chosen model (open it for the timeline). */
  sessionId: string | null;
  /** How it was matched: through a payment platform, or the site's own `odo('purchase')` call. */
  via: ConversionVia;
  /** Matched through a Verified source (odo read the platform itself): it keeps the seal. */
  verified?: boolean;
}

export interface AttributionSummary {
  /** Revenue matched to visits in the range. */
  revenue: number;
  customers: number;
  /** Stripe payments in the range odo couldn't match to a visit (display currency). */
  unattributed: number;
  /** Median first-visit-to-payment time, ms. */
  medianTimeToConvert: number | null;
  /** Average visits before paying. */
  avgVisits: number | null;
  /** Whether a Stripe source in the scope can be matched (has a site to match to). */
  stripeConnected: boolean;
  /** Why payments aren't being traced right now (a key without read access), if so. */
  issue: string | null;
}

export interface TrafficPayload {
  generatedAt: number;
  currency: string;
  timezone: string;
  /**
   * Whether the scope's earnings are before or after fees. Sales from visits are always the
   * amount charged (before fees), so with `net` they say they're gross.
   */
  basis: 'gross' | 'net';
  range: {
    key: TrafficRange;
    from: number;
    to: number;
    granularity: Granularity;
    /** What `previous` is (the query's choice, or the span just before when that can't apply). */
    compare: Compare;
    /**
     * The span `previous` covers: from the same point of the period before, as long as this
     * range so far (real time), never past that period's end.
     */
    previousFrom: number;
    previousTo: number;
  };
  /**
   * The first bucket with visits ever recorded on these sites (null: none yet):
   * earlier buckets were before tracking began, not empty.
   */
  firstKey: string | null;
  /** When the first visit was recorded: a comparison with a span before it would be misleading. */
  firstSeen: number | null;
  /**
   * 1: every visitor counted. More: a big range, estimated from 1 visitor in
   * this many (counts scaled back up; rates and shares as measured).
   */
  sample: number;
  model: AttributionModel;
  totals: TrafficTotals;
  /** The same span just before (for deltas), cut at the same point as this one. */
  previous: TrafficTotals;
  series: TrafficPoint[];
  /**
   * The span before, bucket by bucket, each bucket whole (all of yesterday, for drawing beside
   * today): unlike `previous`, not cut at this range's point.
   */
  previousSeries: TrafficPoint[];
  channels: BreakdownRow[];
  sources: BreakdownRow[];
  campaigns: BreakdownRow[];
  utmSources: BreakdownRow[];
  utmMediums: BreakdownRow[];
  /** utm_content: which version of a link or ad (a post's two captions, an email's two buttons). */
  utmContents: BreakdownRow[];
  pages: BreakdownRow[];
  entryPages: BreakdownRow[];
  exitPages: BreakdownRow[];
  countries: BreakdownRow[];
  regions: BreakdownRow[];
  cities: BreakdownRow[];
  devices: BreakdownRow[];
  browsers: BreakdownRow[];
  oses: BreakdownRow[];
  events: BreakdownRow[];
  goals: GoalResult[];
  attribution: AttributionSummary;
  customers: CustomerJourney[];
  /** Visits by local weekday (0 = Sunday) × hour, for the pattern grid. */
  heatmap: number[][];
  /** First visits vs visits from people who had been before. */
  returning: { new: number; returning: number };
  /** Where visitors were, for the globe (the busiest places first). */
  places: TrafficPlace[];
  fxApproximate: boolean;
}

/**
 * A spot on the globe: roughly where some visitors were (a city, from the
 * proxy or the browser's time zone; the middle of the country for visits
 * recorded before odo kept this).
 */
export interface TrafficPlace {
  lat: number;
  lon: number;
  country: string | null;
  /** The state or region, when the proxy or the IP lookup said. */
  region: string | null;
  /** The city when known (the proxy's or the IP lookup's), or a time-zone area ("New York area"). */
  city: string | null;
  visitors: number;
}

// ─── Live ───────────────────────────────────────────────────────────────────

/**
 * Online: active in the last 3 minutes. The script says "still here" every
 * minute while a page is in front of someone, so leaving (or switching away)
 * drops a visitor within 3 minutes, and reading (or a quick look at another
 * tab) doesn't.
 */
export const ONLINE_MS = 3 * 60_000;

/** Faces on the live map at most (the latest): with this many, there may be more ("500+"). */
export const MAP_PEOPLE = 500;

export type LiveHitKind = 'pageview' | 'event' | 'payment';

export interface LiveHit {
  id: string;
  at: number;
  kind: LiveHitKind;
  /** The visitor's face (as on the live map); null for a payment with no visit. */
  seed: number | null;
  /** Event name (events), else null. */
  name: string | null;
  path: string;
  hostname: string;
  sessionId: string;
  country: string | null;
  city: string | null;
  device: string;
  browser: string;
  source: string | null;
  channel: string;
  /** Payments: display currency. */
  amount: number | null;
}

export interface TrafficLive {
  at: number;
  /** Visitors on the sites now: active in the last 3 minutes (the script says "still here" each minute). */
  online: number;
  /** Visitors per minute, oldest first, for the last 30 minutes. */
  minutes: number[];
  /** Pages people are on right now. */
  pages: { path: string; visitors: number }[];
  recent: LiveHit[];
  /** Where the last 30 minutes' visitors are; `online` of them on the site now, and the latest few. */
  places: (TrafficPlace & { online: number; people: LiveVisitor[] })[];
  /** The last 30 minutes' visitors, latest first, each where their address puts them (the live map). */
  visitors: LiveVisitor[];
  currency: string;
}

/** Someone on a site in the last 30 minutes: where they are now and how they came. */
export interface LiveVisitor {
  sessionId: string;
  /** Stable for a visitor (not their id): picks the face they're drawn with. */
  seed: number;
  /** On the site now (active in the last 3 minutes), not just earlier in the half hour. */
  online: boolean;
  /** Where they are: their city when the address says, else their country or time zone. */
  lat: number;
  lon: number;
  country: string | null;
  region: string | null;
  city: string | null;
  /** The page they're on (their latest). */
  path: string;
  source: string | null;
  channel: string;
  device: string;
  browser: string;
  startedAt: number;
  lastAt: number;
  pageviews: number;
}

// ─── Visits ─────────────────────────────────────────────────────────────────

export interface SessionSummary {
  id: string;
  /** A short, stable label for the visitor (not their id). */
  visitor: string;
  startedAt: number;
  endedAt: number;
  /** Engaged seconds. */
  duration: number;
  pageviews: number;
  events: number;
  entry: string;
  exit: string;
  hostname: string;
  source: string | null;
  channel: string;
  campaign: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  device: string;
  browser: string;
  os: string;
  language: string | null;
  /** Visited before this visit. */
  returning: boolean;
  /** Paid during or right after this visit (display currency). */
  revenue: number;
}

export interface SessionPage {
  sessions: SessionSummary[];
  /** Pass as `before` for the next page; null at the end. */
  next: number | null;
}

export interface TimelineItem {
  at: number;
  kind: LiveHitKind | 'engagement';
  path: string;
  name: string | null;
  /** Pageviews: time on the page (s) and how far down they scrolled (%). */
  engaged: number | null;
  scroll: number | null;
  props: Record<string, string> | null;
  amount: number | null;
}

export interface SessionDetail {
  session: SessionSummary;
  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    term: string | null;
    content: string | null;
  };
  referrerHost: string | null;
  /** This visitor's visits (all time) and when they first came. */
  visits: number;
  firstSeenAt: number;
  timeline: TimelineItem[];
  /** What they did on its pages (clicks, forms, errors…), in order. */
  interactions?: InteractionItem[];
  /** Other visits by the same visitor, newest first. */
  history: SessionSummary[];
}

export interface FunnelResult {
  id: string;
  name: string;
  /** As TrafficPayload.sample: counted 1 visitor in this many (1: all). */
  sample: number;
  steps: {
    label: string;
    kind: GoalKind;
    match: string;
    visitors: number;
    conversion: number;
    dropoff: number;
  }[];
}

// ─── Summary (the hero's visitors) ──────────────────────────────────────────

export interface TrafficPeriodSummary {
  /** When it was counted: the period so far runs to here, and the stretch before is as long. */
  at: number;
  visitors: number;
  visits: number;
  pageviews: number;
  /**
   * The same stretch of the period before (null, all three: nothing was recorded back then,
   * decided as `comparable` decides it).
   */
  previousVisitors: number | null;
  previousVisits: number | null;
  previousPageviews: number | null;
}

/** Today, this month and this year so far, for the big number when it counts visitors. */
export interface TrafficSummary {
  /** When today's numbers were counted (each period says when it was counted). */
  at: number;
  periods: Record<'day' | 'month' | 'year', TrafficPeriodSummary>;
}

/**
 * Whether a dashboard counts what the summary's day counts: today so far against yesterday,
 * every visit (no filters) and every visitor (not estimated). For the same site, the two are
 * then one reading, and the newer one is shown in both places.
 */
export function sameAsToday(
  p: { range: { key: TrafficRange; compare: Compare }; sample: number },
  filters: TrafficFilters,
): boolean {
  return (
    p.range.key === 'today' &&
    p.range.compare === 'previous' &&
    p.sample === 1 &&
    Object.keys(filters).length === 0
  );
}

type Counts = Pick<TrafficTotals, 'visitors' | 'visits' | 'pageviews'>;

/** A dashboard's today so far, as the summary's day (when `sameAsToday`). */
export function daySummaryOf(p: {
  generatedAt: number;
  range: { compare: Compare; previousFrom: number };
  firstSeen: number | null;
  totals: Counts;
  previous: Counts;
}): TrafficPeriodSummary {
  const known = comparable(p);
  return {
    at: p.generatedAt,
    visitors: p.totals.visitors,
    visits: p.totals.visits,
    pageviews: p.totals.pageviews,
    previousVisitors: known ? p.previous.visitors : null,
    previousVisits: known ? p.previous.visits : null,
    previousPageviews: known ? p.previous.pageviews : null,
  };
}

// ─── Comparisons ────────────────────────────────────────────────────────────

/** Visits recorded this soon after an earlier span began still count as recorded from its start. */
const TRACKING_GRACE_MS = 3_600_000;

/** Whether visits were already being recorded when a span began (an hour's grace). */
export const trackedSince = (firstSeen: number | null, from: number): boolean =>
  firstSeen !== null && firstSeen <= from + TRACKING_GRACE_MS;

/**
 * Whether a comparison means something: there is one, and visits were already being
 * recorded when the span it compares with began. The dashboard, the top's visitors and AI
 * assistants all decide it this way.
 */
export function comparable(stats: {
  range: { compare: Compare; previousFrom: number };
  firstSeen: number | null;
}): boolean {
  return stats.range.compare !== 'none' && trackedSince(stats.firstSeen, stats.range.previousFrom);
}

// ─── Event details ──────────────────────────────────────────────────────────

/** One event's details (`odo('Signup', { plan: 'Pro' })`): each property and its values, for the range. */
export interface EventProperties {
  name: string;
  count: number;
  visitors: number;
  /** Most sent first; each property's values most common first. */
  properties: {
    key: string;
    values: { value: string; count: number; visitors: number }[];
    /** Sends with values beyond those listed. */
    other: number;
  }[];
}

// ─── Ad earnings by country ─────────────────────────────────────────────────

export interface AdCountry {
  /** ISO code ('' when the ad network doesn't know). */
  country: string;
  impressions: number;
  clicks: number;
  /** Display currency. */
  revenue: number;
  /** Revenue per 1,000 impressions. */
  cpm: number;
}

/** What ad sources (Adsterra) earned in the range, country by country: next to visitors, that's revenue per visitor. */
export interface AdGeoPayload {
  generatedAt: number;
  currency: string;
  /** Any ad source in this scope. */
  connected: boolean;
  /**
   * Each ad account counts only its domains that are these websites. False
   * when an account has none of them, so all of its earnings are counted.
   */
  matched: boolean;
  /** The days asked for, in the ad network's own calendar (Adsterra's are UTC days). */
  from: string;
  to: string;
  /** The account's time zone, for saying when a network's day begins. */
  timezone: string;
  /** The ad networks counted, by name ("Adsterra"). */
  networks: string[];
  totals: { impressions: number; clicks: number; revenue: number; cpm: number };
  /** Most revenue first. */
  countries: AdCountry[];
  /**
   * None of the range's days has been reported yet: the ad network's day hasn't begun (just
   * after midnight, east of UTC), though earnings may already count part of it.
   */
  pending: boolean;
  /** While pending: when the ad network's first day of the range begins (epoch ms), else null. */
  pendingUntil: number | null;
  /** Why some or all of it couldn't be read, if so. */
  issue: string | null;
}
